import React, { useState, useEffect } from 'react';
import { Modal } from '../design/Modal';
import { Button } from '../design/Button';
import { Input } from '../design/Input';
import { Search, Download, Loader2, Package, Server, Monitor, Check } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import toast from 'react-hot-toast';
import { FunLoader } from '../common/FunLoader';
import { formatBytes } from '../../utils/format';

interface ModBrowserModalProps {
    isOpen: boolean;
    onClose: () => void;
    serverId: string;
    mcVersion: string;
    // Server-Loader als START-Auswahl; im Modal frei umstellbar (zukunftssicher).
    loader: string;
    type: 'mod' | 'datapack' | 'plugin';
}

// Bukkit-API-Plugin-Familie (ein Plugin ist meist mit mehreren getaggt).
const PLUGIN_LOADERS = ['paper', 'purpur', 'spigot', 'bukkit', 'folia'];
// Beliebte Mod-Loader zuerst (Rest kommt dynamisch aus der API dahinter).
const LOADER_PRIORITY = ['neoforge', 'forge', 'fabric', 'quilt'];

interface ModrinthLoader { name: string; icon?: string; supported_project_types?: string[]; }
interface ModrinthProject {
    project_id: string; slug: string; title: string; description: string;
    categories: string[]; client_side: string; server_side: string; project_type: string;
    downloads: number; icon_url?: string; author: string; versions: string[];
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; title?: string }> = ({ active, onClick, children, title }) => (
    <button onClick={onClick} title={title}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${active
            ? 'bg-ember text-white border-ember shadow-[0_0_12px_rgba(255,106,61,0.35)]'
            : 'bg-surface-2 text-fg-muted border-line hover:text-white hover:border-line-strong'}`}>
        {children}
    </button>
);

export const ModBrowserModal: React.FC<ModBrowserModalProps> = ({
    isOpen, onClose, serverId, mcVersion, loader, type
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<ModrinthProject[]>([]);
    const [loading, setLoading] = useState(false);
    const [installing, setInstalling] = useState<string | null>(null);
    const [installed, setInstalled] = useState<Set<string>>(new Set());
    // Bereits auf dem Server installierte Modrinth-Projekte (per Datei-Hash
    // erkannt) -> "Installiert"-Badge, verhindert Doppel-Installationen.
    const [onDisk, setOnDisk] = useState<Set<string>>(new Set());

    const [allLoaders, setAllLoaders] = useState<ModrinthLoader[]>([]);
    const [selectedLoader, setSelectedLoader] = useState<string>(loader || 'fabric');
    const [serverOnly, setServerOnly] = useState(type === 'mod');

    // Mod-Loader dynamisch aus der API, beliebte zuerst -> zukunftssichere Chips.
    const loaderChips = allLoaders
        .filter((l) => (l.supported_project_types || []).includes('mod'))
        .map((l) => l.name)
        .filter((n) => !PLUGIN_LOADERS.includes(n) || type === 'plugin')
        .sort((a, b) => {
            const ia = LOADER_PRIORITY.indexOf(a), ib = LOADER_PRIORITY.indexOf(b);
            if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            return a.localeCompare(b);
        });
    const loaderNames = allLoaders.map((l) => l.name);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedLoader(loader || 'fabric');
        setServerOnly(type === 'mod');
        setInstalled(new Set());
        setOnDisk(new Set());
        (async () => {
            try {
                const res = await apiFetch('/api/modrinth/loaders');
                if (res.ok) { const d = await res.json(); if (Array.isArray(d.loaders)) setAllLoaders(d.loaders); }
            } catch { /* faellt auf Server-Loader zurueck */ }
        })();
        // Bereits installierte Projekte per Hash aufloesen (dauert beim ersten
        // Mal ein paar Sekunden bei grossen Packs, danach gecacht).
        if (type !== 'datapack') {
            const folder = type === 'plugin' ? 'plugins' : 'mods';
            (async () => {
                try {
                    const res = await apiFetch(`/api/servers/${serverId}/mods/installed-projects?folder=${folder}`);
                    if (res.ok) {
                        const d = await res.json();
                        if (Array.isArray(d.projectIds)) setOnDisk(new Set(d.projectIds));
                    }
                } catch { /* Badge entfaellt dann */ }
            })();
        }
        searchMods(loader || 'fabric');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const searchMods = async (loaderArg?: string) => {
        const activeLoader = loaderArg ?? selectedLoader;
        setLoading(true);
        try {
            const facets: string[][] = [[`project_type:${type}`]];
            if (type === 'mod' && activeLoader && activeLoader !== 'all') facets.push([`categories:${activeLoader}`]);
            else if (type === 'plugin') facets.push(PLUGIN_LOADERS.map((l) => `categories:${l}`));
            const index = query ? 'relevance' : 'downloads';
            const res = await apiFetch(`/api/modrinth/search?query=${encodeURIComponent(query)}&facets=${JSON.stringify(facets)}&index=${index}&limit=40`);
            if (res.ok) { const data = await res.json(); setResults(data.hits || []); }
        } catch (e) {
            console.error(e); toast.error('Suche fehlgeschlagen');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!isOpen) return;
        const timer = setTimeout(() => searchMods(), 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, selectedLoader]);

    const handleInstall = async (project: ModrinthProject) => {
        setInstalling(project.project_id);
        try {
            const installLoader = selectedLoader === 'all' ? (loader || 'fabric') : selectedLoader;
            const queryLoaders = type === 'plugin' ? PLUGIN_LOADERS : type === 'datapack' ? ['datapack'] : [installLoader];
            const versionsRes = await apiFetch(`/api/modrinth/versions/${project.project_id}?loaders=${JSON.stringify(queryLoaders)}&game_versions=["${mcVersion}"]`);
            if (!versionsRes.ok) throw new Error('Failed to fetch versions');
            const versions = (await versionsRes.json()).versions;
            if (!versions || versions.length === 0) { toast.error(`Keine ${cap(installLoader)}-Version fuer MC ${mcVersion} gefunden`); return; }
            const latest = versions[0];
            const file = latest.files.find((f: any) => f.primary) || latest.files[0];
            if (!file) { toast.error('Keine Datei gefunden'); return; }
            await apiFetch(`/api/servers/${serverId}/mods`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: file.url, filename: file.filename, projectId: project.project_id, versionName: latest.name, folder: type === 'plugin' ? 'plugins' : type === 'datapack' ? 'world/datapacks' : 'mods' })
            });
            setInstalled((s) => new Set(s).add(project.project_id));
            toast.success(`${project.title} installiert!`);
        } catch (e) {
            console.error(e); toast.error('Installation fehlgeschlagen');
        } finally { setInstalling(null); }
    };

    const visible = serverOnly ? results.filter((p) => p.server_side !== 'unsupported') : results;
    const title = `${type === 'mod' ? 'Mods' : type === 'datapack' ? 'Datapacks' : 'Plugins'} durchsuchen`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl" className="md:max-w-5xl max-h-[90vh]">
            <div className="flex flex-col gap-4">
                {/* Suche + Filter */}
                <div className="flex flex-col gap-3 sticky top-0 bg-surface z-10 pb-3 border-b border-line">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-muted" size={18} />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Suche… (z.B. Sodium, Create, JEI)" className="pl-11 h-11 text-base" autoFocus />
                    </div>
                    {type === 'mod' && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase font-bold text-fg-muted mr-1">Loader</span>
                            <Chip active={selectedLoader === 'all'} onClick={() => setSelectedLoader('all')}>Alle</Chip>
                            {loaderChips.map((l) => (
                                <Chip key={l} active={selectedLoader === l} onClick={() => setSelectedLoader(l)}>{cap(l)}</Chip>
                            ))}
                            <div className="ml-auto">
                                <Chip active={serverOnly} onClick={() => setServerOnly((v) => !v)} title="Client-only-Mods ausblenden">
                                    <span className="flex items-center gap-1"><Server size={12} /> Nur server-seitig</span>
                                </Chip>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-fg-muted">
                        <span className="bg-surface-2 px-2 py-0.5 rounded border border-line">MC {mcVersion}</span>
                        <span className="ml-auto">{query ? `${visible.length} Ergebnisse` : 'Beliebteste'}</span>
                    </div>
                </div>

                {/* Karten-Raster */}
                <div className="min-h-[320px]">
                    {loading ? (
                        <div className="flex items-center justify-center py-24"><FunLoader size="md" showProgress={false} /></div>
                    ) : visible.length === 0 ? (
                        <div className="text-center text-fg-muted py-24 flex flex-col items-center">
                            <Package size={48} className="mb-4 opacity-20" />
                            <p>Keine Ergebnisse{type === 'mod' && selectedLoader !== 'all' ? ` fuer ${cap(selectedLoader)}` : ''}.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {visible.map((project) => {
                                const projLoaders = (project.categories || []).filter((c) => loaderNames.includes(c));
                                const clientOnly = project.server_side === 'unsupported';
                                const serverReq = project.server_side === 'required';
                                const isDone = installed.has(project.project_id) || onDisk.has(project.project_id);
                                return (
                                    <div key={project.project_id} className="bg-surface-2 border border-line rounded-2xl p-4 flex flex-col gap-3 hover:border-ember/40 hover:bg-white/[0.03] transition-all">
                                        <div className="flex gap-3">
                                            <div className="w-14 h-14 bg-[#0F1016] rounded-xl shrink-0 overflow-hidden border border-line">
                                                {project.icon_url
                                                    ? <img src={project.icon_url} alt={project.title} className="w-full h-full object-cover" />
                                                    : <div className="w-full h-full flex items-center justify-center text-fg-muted"><Package size={22} /></div>}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-bold text-[15px] text-white truncate">{project.title}</h3>
                                                <p className="text-[11px] text-fg-muted truncate">von {project.author}</p>
                                                <div className="flex items-center gap-1 mt-1 text-[11px] text-fg-muted">
                                                    <Download size={11} />{formatBytes(project.downloads, 0).replace(' B', '')}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-[13px] text-fg-muted line-clamp-2 min-h-[36px] leading-snug">{project.description}</p>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {projLoaders.slice(0, 4).map((l) => (
                                                <span key={l} className="text-[10px] bg-ember/10 text-ember px-2 py-0.5 rounded-md border border-ember/20 capitalize">{l}</span>
                                            ))}
                                            {serverReq && <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded-md border border-green-500/20 flex items-center gap-1"><Server size={10} /> Server</span>}
                                            {clientOnly && <span className="text-[10px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded-md border border-red-500/20 flex items-center gap-1"><Monitor size={10} /> Nur Client</span>}
                                        </div>
                                        <Button size="sm"
                                            className={`w-full mt-auto ${isDone ? 'bg-green-600/20 text-green-400 border border-green-500/30' : installing === project.project_id ? 'bg-ember/20 text-ember' : 'bg-ember hover:bg-ember/80 text-white'}`}
                                            onClick={() => handleInstall(project)}
                                            disabled={!!installing || clientOnly || isDone}>
                                            {isDone ? <><Check size={16} className="mr-1.5" /> Installiert</>
                                                : installing === project.project_id ? <Loader2 className="animate-spin" size={16} />
                                                : <><Download size={16} className="mr-1.5" /> Installieren</>}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
